"use strict";
/**
 * The `pf` URL hint that tells the WEBSITE which client a sign-in belongs to.
 *
 * Why this exists: an extension sign-in physically happens in a website popup
 * (the landing iframe opens `{website}/sign-in/verify`, which hops to
 * `/sign-in`, then out to the provider, then back to `/sign-in/callback`). By
 * the time the callback page runs, nothing in its own URL or origin
 * distinguishes "the user is on skim.plus" from "the user is in the browser
 * extension" — both are the same page on the same origin.
 *
 * So the originating surface tags the popup URL with `?pf=extension`, and the
 * value rides the SAME hop chain the `aa` (auth_attempt_id) param already
 * travels, ending up in the popup tab's sessionStorage attempt record — which
 * is the one store that survives the cross-origin OAuth redirect dance.
 *
 * Deliberately NOT inferred from `window.opener` / `document.referrer`: Google's
 * COOP header severs the opener mid-OAuth, which is the entire reason the
 * BroadcastChannel + popup-close rescue machinery exists. Opener-based inference
 * would be exactly as unreliable as the bug class this codebase already fought.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.appendPlatformHint = exports.isEventPlatform = exports.platformToHint = exports.parsePlatformHint = exports.EXTENSION_PLATFORM_HINT = exports.DEFAULT_WEB_PLATFORM = exports.PLATFORM_HINT_PARAM = void 0;
const types_1 = require("./types");
/** Query-param name. Short because it rides length-capped URLs (Apple state). */
exports.PLATFORM_HINT_PARAM = 'pf';
/**
 * Accepted hint values. A strict allowlist — the raw value is never
 * interpolated anywhere, and anything unrecognised degrades to the default
 * rather than being trusted.
 */
const PLATFORM_HINT_VALUES = {
    extension: 'BROWSER_EXTENSION',
    website: 'WEBSITE',
};
/**
 * The platform a sign-in belongs to when no hint is present. A bare visit to
 * the website is by far the common case, so absence means WEBSITE.
 */
exports.DEFAULT_WEB_PLATFORM = 'WEBSITE';
/** Hint value the extension surfaces append. */
exports.EXTENSION_PLATFORM_HINT = 'extension';
/**
 * Map a raw `pf` value to a platform. Unknown / absent / malformed → null so
 * callers can distinguish "no hint given" from "hint said website".
 */
const parsePlatformHint = (raw) => {
    if (typeof raw !== 'string' || raw.length === 0)
        return null;
    return PLATFORM_HINT_VALUES[raw.toLowerCase()] ?? null;
};
exports.parsePlatformHint = parsePlatformHint;
/**
 * Reverse mapping, for surfaces that hold a platform and need to tag a URL.
 */
const platformToHint = (platform) => {
    if (platform === 'BROWSER_EXTENSION')
        return exports.EXTENSION_PLATFORM_HINT;
    if (platform === 'WEBSITE')
        return 'website';
    return null;
};
exports.platformToHint = platformToHint;
/**
 * Validate a value that was round-tripped through storage. Used when reading the
 * platform back off the persisted auth-attempt record — never trust the blob.
 */
const isEventPlatform = (value) => typeof value === 'string' &&
    types_1.EVENT_PLATFORMS.includes(value);
exports.isEventPlatform = isEventPlatform;
/**
 * Append the platform hint to a URL, mirroring how `appendAuthAttemptId` threads
 * `aa` (same `?` vs `&` handling). Purely additive and fail-open: on any problem
 * the original URL is returned unchanged, so hint plumbing can never block a
 * sign-in.
 */
const appendPlatformHint = (url, hint) => {
    try {
        if (typeof url !== 'string' || url.length === 0)
            return url;
        if (typeof hint !== 'string' || hint.length === 0)
            return url;
        const separator = url.includes('?') ? '&' : '?';
        return `${url}${separator}${exports.PLATFORM_HINT_PARAM}=${encodeURIComponent(hint)}`;
    }
    catch {
        return url;
    }
};
exports.appendPlatformHint = appendPlatformHint;
